import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core';
import type {
    ProofReadingProgressService,
    ProofReadingProgressData,
    ProofReadingProgressComputed,
} from '../proof-reading-progress-service';
import { ProofReadingOrchestrationService } from '../proof-reading-orchestration-service';
import { I18nService } from '@telecom/shared/services/i18n';
import type { SpecChapter } from '../../types';
import { AUTO_CLOSE_COUNTDOWN_INITIAL_VALUE } from '../../constants';

@injectable()
export class ProofReadingProgressServiceImpl implements ProofReadingProgressService {
    private readonly _onProgressDataChangeEmitter = new Emitter<ProofReadingProgressData>();
    private readonly _onComputedValuesChangeEmitter = new Emitter<ProofReadingProgressComputed>();

    private _progressData: ProofReadingProgressData = {
        status: 'idle',
        currentChapterName: '',
        currentChapterIndex: 0,
        totalChapters: 0,
        currentParagraphIndex: 0,
        totalParagraphs: 0,
        totalIssuesFound: 0,
        isVisible: false,
        autoCloseCountdown: 0,
    };

    private _disposables: Array<{ dispose(): void }> = [];
    private _autoCloseTimer: NodeJS.Timeout | null = null;
    private _chapters: SpecChapter[] = [];

    constructor(
        @inject(I18nService)
        private readonly _i18nService: I18nService,
        @inject(ProofReadingOrchestrationService)
        private readonly _proofReadingOrchestrationService: ProofReadingOrchestrationService,
    ) {}

    get onProgressDataChange(): Event<ProofReadingProgressData> {
        return this._onProgressDataChangeEmitter.event;
    }

    get onComputedValuesChange(): Event<ProofReadingProgressComputed> {
        return this._onComputedValuesChangeEmitter.event;
    }

    getProgressData(): ProofReadingProgressData {
        return { ...this._progressData };
    }

    // eslint-disable-next-line complexity
    getComputedValues(): ProofReadingProgressComputed {
        const { status, currentParagraphIndex, totalParagraphs, totalIssuesFound } = this._progressData;

        // 计算进度百分比
        let progressPercentage = 0;
        if (status === 'completed') {
            progressPercentage = 100;
        } else if (totalParagraphs > 0) {
            progressPercentage = Math.round((currentParagraphIndex / totalParagraphs) * 100);
        }

        const t = this._i18nService.t.bind(this._i18nService);

        // 生成进度文本
        let progressText = '';
        switch (status) {
        case 'idle':
            // cn: 准备开始核稿
            // tw: 準備開始核稿
            // en: Ready to start proofreading
            // jp: 核稿を開始する準備
            progressText = t('proofReading.progress.readyToStartProofreading');
            break;
        case 'loading':
            // cn: 正在加载章节内容
            // tw: 正在加載章節內容
            // en: Loading chapter content
            // jp: 章節内容を読み込んでいます
            progressText = t('proofReading.progress.loadingChapterContent');
            break;
        case 'checking':
            // cn: 正在逐段核稿{count}/{total}，已发现{totalIssuesFound}个问题
            // tw: 正在逐段核稿{count}/{total}，已發現{totalIssuesFound}個問題
            // en: Checking paragraph {count}/{total}, found {totalIssuesFound} issues
            // jp: 段落を逐次確認しています {count}/{total}、{totalIssuesFound} 個の問題を発見しました
            progressText = t('proofReading.progress.checkingChapter', {
                count: currentParagraphIndex,
                total: totalParagraphs,
                totalIssuesFound,
            });
            break;
        case 'completed':
            // cn: 已检查完段落 {count} 个，共发现 {totalIssuesFound} 个问题
            // tw: 已檢查完段落 {count} 個，共發現 {totalIssuesFound} 個問題
            // en: Checked {count} paragraphs, found {totalIssuesFound} issues
            // jp: {count} 段落を確認しました、{totalIssuesFound} 個の問题を発見しました
            progressText = t('proofReading.progress.proofreadingCompleted', {
                count: totalParagraphs,
                totalIssuesFound,
            });
            break;
        case 'error':
            // cn: 核稿过程中出现错误
            // tw: 核稿過程中出現錯誤
            // en: Error occurred during proofreading
            // jp: 核稿中にエラーが発生しました
            progressText = t('proofReading.progress.errorOccurredDuringProofreading');
            break;
        }

        return {
            progressText,
            showStopButton: status === 'checking' || status === 'loading',
            showAutoCloseCountdown: this._progressData.autoCloseCountdown > 0,
            progressPercentage,
        };
    }

    stop(): void {
        this._proofReadingOrchestrationService.abortFindIssues();
        this._updateProgressData({ status: 'idle' });
        this.hide();
    }

    hide(): void {
        this._updateProgressData({ isVisible: false, autoCloseCountdown: 0 });
        if (this._autoCloseTimer) {
            clearInterval(this._autoCloseTimer);
            this._autoCloseTimer = null;
        }
    }

    // eslint-disable-next-line max-lines-per-function
    initialize(): void {
        // 订阅 ProofReadingOrchestrationService 的事件
        this._disposables.push(
            this._proofReadingOrchestrationService.onChaptersLoad(() => {
                this._updateProgressData({
                    status: 'loading',
                    isVisible: true,
                    currentChapterName: '',
                    currentChapterIndex: 0,
                    totalChapters: 0,
                    currentParagraphIndex: 0,
                    totalParagraphs: 0,
                    totalIssuesFound: 0,
                });
            }),

            this._proofReadingOrchestrationService.onDidChaptersLoad((event) => {
                // 存储章节数据用于计算段落索引
                this._chapters = event.chapters;
                // 计算总段落数
                const totalParagraphs = event.chapters.reduce((total, chapter) => {
                    return total + chapter.paragraphs.length;
                }, 0);
                this._updateProgressData({
                    totalChapters: event.chapters.length,
                    totalParagraphs,
                });
            }),

            this._proofReadingOrchestrationService.onChapterCheckStarted((event) => {
                // 计算当前段落索引（累加到当前章节的所有段落）
                const currentParagraphIndex = this._calculateCurrentParagraphIndex(event.chapterIndex);
                const updateData: Partial<ProofReadingProgressData> = {
                    status: 'checking',
                    currentChapterName: event.chapterType,
                    currentChapterIndex: event.chapterIndex + 1,
                    totalChapters: event.totalChapters,
                    currentParagraphIndex,
                    isVisible: true,
                };

                // 开始检查第一个章节时重置问题计数器，确保统计准确
                if (event.chapterIndex === 0) {
                    updateData.totalIssuesFound = 0;
                }

                this._updateProgressData(updateData);
            }),

            this._proofReadingOrchestrationService.onChapterIssuesFound((event) => {
                const totalIssuesFound = this._progressData.totalIssuesFound + event.issueGroups.reduce((acc, issueGroup) => {
                    // 只计算未被忽略的问题
                    const nonIgnoredIssues = issueGroup.issues.filter(issue => !issue.isIgnored);
                    return acc + nonIgnoredIssues.length;
                }, 0);
                this._updateProgressData({
                    totalIssuesFound,
                });
            }),

            this._proofReadingOrchestrationService.onProofReadingCompleted(() => {
                this._updateProgressData({
                    status: 'completed',
                });

                // 启动自动关闭倒计时
                this._startAutoCloseCountdown();
            }),

            this._proofReadingOrchestrationService.onProofReadingError(() => {
                this._updateProgressData({
                    status: 'error',
                    isVisible: true,
                });
            }),

            this._proofReadingOrchestrationService.onExecutionStatusChanged((event) => {
                // 如果执行状态变为非执行状态且当前状态为checking，则更新为completed
                if (!event.isExecuting && this._progressData.status === 'checking') {
                    this._updateProgressData({
                        status: 'completed',
                    });

                    // 启动自动关闭倒计时
                    this._startAutoCloseCountdown();
                }
            }),
        );
    }

    dispose(): void {
        // 清理所有订阅
        this._disposables.forEach(disposable => disposable.dispose());
        this._disposables = [];

        // 清理定时器
        if (this._autoCloseTimer) {
            clearInterval(this._autoCloseTimer);
            this._autoCloseTimer = null;
        }
    }

    private _updateProgressData(updates: Partial<ProofReadingProgressData>): void {
        this._progressData = { ...this._progressData, ...updates };
        this._onProgressDataChangeEmitter.fire(this._progressData);

        // 同时触发计算属性变化事件
        const computedValues = this.getComputedValues();
        this._onComputedValuesChangeEmitter.fire(computedValues);
    }

    private _startAutoCloseCountdown(): void {
        // 重置倒计时
        this._updateProgressData({ autoCloseCountdown: AUTO_CLOSE_COUNTDOWN_INITIAL_VALUE });

        // 启动倒计时
        this._autoCloseTimer = setInterval(() => {
            const newCountdown = this._progressData.autoCloseCountdown - 1;
            this._updateProgressData({ autoCloseCountdown: newCountdown });

            if (newCountdown <= 0) {
                if (this._autoCloseTimer) {
                    clearInterval(this._autoCloseTimer);
                    this._autoCloseTimer = null;
                }
                this.hide();
            }
        }, 1000);
    }

    /**
     * 计算当前段落索引（累计已处理的段落数）
     * @param chapterIndex 当前章节索引（从0开始）
     * @returns 当前段落索引（从1开始，用于显示）
     */
    private _calculateCurrentParagraphIndex(chapterIndex: number): number {
        if (!this._chapters || this._chapters.length === 0) {
            return 0;
        }

        // 累加前面所有章节的段落数
        let paragraphCount = 0;
        for (let i = 0; i < chapterIndex && i < this._chapters.length; i++) {
            paragraphCount += this._chapters[i].paragraphs.length;
        }

        // 加上当前章节的段落数（假设当前章节已开始处理，所以至少有1个段落在处理）
        if (chapterIndex < this._chapters.length) {
            paragraphCount += this._chapters[chapterIndex].paragraphs.length;
        }

        return paragraphCount;
    }
}
